import { useCallback } from 'react';
import type { VehicleByRegistrationNumber } from '@//app/api/protected/vehicle/lookup/registration-number/[registrationNumber]';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import { useMarket } from '@//providers/MarketSettingsProvider';
import type { LegacyTrackShownInterestEventData } from './legacy-types';
import { mapAnythingToLegacyItem } from './utils';

// Module-level Set to track vehicle IDs across all hook instances in the session
const trackedVehicleIds = new Set<string>();

export const createLegacyTrackShownInterest = (
  market: Market,
  vehicle: ElasticVehicle | VehicleByRegistrationNumber | ElasticListVehicle,
): LegacyTrackShownInterestEventData => {
  const eventData: LegacyTrackShownInterestEventData = {
    eventType: 'shownInterest',
    item: mapAnythingToLegacyItem(market, vehicle),
  };

  if ('id' in vehicle && vehicle.id) {
    eventData.pageType = 'product page';
  }

  return eventData;
};

export const useLegacyTrackShownInterest = () => {
  const market = useMarket();
  const { track } = useLegacyTracking();

  const trackLegacyShownInterest = useCallback(
    (vehicle: ElasticVehicle | VehicleByRegistrationNumber | ElasticListVehicle) => {
      const vehicleId = 'id' in vehicle ? vehicle.id : undefined;

      // Only track if vehicle has an ID and hasn't been tracked yet in this session
      if (!vehicleId || trackedVehicleIds.has(vehicleId)) {
        return;
      }

      trackedVehicleIds.add(vehicleId);
      track({
        event: 'shownInterest',
        data: createLegacyTrackShownInterest(market, vehicle),
      });
    },
    [market, track],
  );

  return {
    trackLegacyShownInterest,
  };
};
